"use client";

import { useEffect, useState } from "react";
import { FinWordStats as StatsType } from "@/lib/daily/types";
import { formatTime } from "@/lib/daily/dateUtils";
import { generateFinWordShare, generateShareCardCanvas } from "@/lib/daily/shareGenerator";
import ShareButton from "../shared/ShareButton";
import { Trophy, Award } from "lucide-react";

interface FinWordStatsProps {
  readonly stats: StatsType;
  readonly dayNumber: number;
  readonly guesses: readonly string[];
  readonly solution: string;
  readonly won: boolean;
  readonly isGameOver: boolean;
  readonly startTime: number;
  readonly endTime?: number;
  readonly mode?: "daily" | "practice";
}

export default function FinWordStats({
  stats,
  dayNumber,
  guesses,
  solution,
  won,
  isGameOver,
  startTime,
  endTime,
  mode = "daily",
}: FinWordStatsProps) {
  const [countdown, setCountdown] = useState("");

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const nextMidnight = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)
      );
      const diff = nextMidnight.getTime() - now.getTime();

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown(
        `${hours}h ${String(minutes).padStart(2, "0")}m ${String(seconds).padStart(2, "0")}s`
      );
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const winPercentage = stats.gamesPlayed > 0 ? Math.round((stats.gamesWon / stats.gamesPlayed) * 100) : 0;
  
  // Find max distribution value for chart scaling
  const maxDistribution = Math.max(...stats.guessDistribution, 1);

  const duration = isGameOver ? (endTime || Date.now()) - startTime : 0;

  // Share Text
  const shareText = isGameOver
    ? generateFinWordShare(dayNumber, guesses, solution, won, stats.currentStreak, duration)
    : "";

  // Share Image Canvas Data URL
  const shareCanvasDataUrl = isGameOver
    ? generateShareCardCanvas(
        `FinWord #${dayNumber} ${won ? `${guesses.length}/6` : "X/6"}`,
        `Solved in ${formatTime(duration)}`,
        "Current Streak",
        `${stats.currentStreak} days`,
        [`Games Played: ${stats.gamesPlayed}`, `Win Rate: ${winPercentage}%`, `Avg Guesses: ${stats.averageGuesses}`]
      )
    : undefined;

  return (
    <div className="finword-status-box anim-fade-in" style={{ marginTop: 0, padding: "0.6rem 0.85rem" }}>
      <h3 style={{ fontSize: "var(--text-sm)", fontWeight: "var(--fw-extrabold)", marginBottom: "0.25rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
        <Trophy size={14} style={{ color: "var(--accent-orange)" }} /> Statistics
      </h3>

      {/* Stats Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.35rem", marginBottom: "0.4rem" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "var(--text-lg)", fontWeight: "var(--fw-extrabold)", lineHeight: 1.1 }}>{stats.gamesPlayed}</div>
          <div style={{ fontSize: "var(--text-2xs)", color: "var(--text-secondary)", fontWeight: "var(--fw-bold)", textTransform: "uppercase" }}>Played</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "var(--text-lg)", fontWeight: "var(--fw-extrabold)", lineHeight: 1.1 }}>{winPercentage}%</div>
          <div style={{ fontSize: "var(--text-2xs)", color: "var(--text-secondary)", fontWeight: "var(--fw-bold)", textTransform: "uppercase" }}>Win %</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "var(--text-lg)", fontWeight: "var(--fw-extrabold)", lineHeight: 1.1 }}>{stats.currentStreak}</div>
          <div style={{ fontSize: "var(--text-2xs)", color: "var(--text-secondary)", fontWeight: "var(--fw-bold)", textTransform: "uppercase" }}>Streak</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "var(--text-lg)", fontWeight: "var(--fw-extrabold)", lineHeight: 1.1 }}>{stats.maxStreak}</div>
          <div style={{ fontSize: "var(--text-2xs)", color: "var(--text-secondary)", fontWeight: "var(--fw-bold)", textTransform: "uppercase" }}>Max</div>
        </div>
      </div>

      {/* Distribution Chart */}
      <h4 style={{ fontSize: "var(--text-2xs)", fontWeight: "var(--fw-extrabold)", textAlign: "left", marginBottom: "0.25rem", textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
        Guess Distribution
      </h4>
      <div style={{ display: "flex", flexDirection: "column", gap: "2px", marginBottom: "0.4rem" }}>
        {stats.guessDistribution.map((count, index) => {
          const percentWidth = (count / maxDistribution) * 100;
          const isCurrentGuessCount = isGameOver && won && guesses.length === index + 1;

          return (
            <div key={index} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{ width: "10px", fontWeight: "var(--fw-bold)", fontSize: "var(--text-2xs)", color: "var(--text-secondary)" }}>
                {index + 1}
              </div>
              <div style={{ flex: 1, height: "12px", backgroundColor: "var(--overlay-a03)", borderRadius: "2px", overflow: "hidden" }}>
                <div
                  style={{
                    width: `${Math.max(percentWidth, 6)}%`,
                    height: "100%",
                    backgroundColor: isCurrentGuessCount ? "var(--primary)" : "var(--text-muted)",
                    color: "var(--text-on-accent)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    paddingRight: "4px",
                    fontWeight: "var(--fw-bold)",
                    fontSize: "var(--text-2xs)",
                    lineHeight: 1,
                    transition: "width 0.5s ease-out",
                  }}
                >
                  {count}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reset & Share Footer */}
      {isGameOver && (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "0.5rem",
            paddingTop: "0.35rem",
            borderTop: "1px solid var(--border-color)",
          }}
        >
          {mode === "daily" ? (
            <>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: "var(--text-2xs)", fontWeight: "var(--fw-bold)", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Next FinWord In
                </div>
                <div style={{ fontSize: "var(--text-sm)", fontWeight: "var(--fw-extrabold)", color: "var(--text-primary)", fontFamily: "monospace", marginTop: "1px" }}>
                  {countdown}
                </div>
              </div>

              <ShareButton
                text={shareText}
                canvasDataUrl={shareCanvasDataUrl}
                url="https://financelives.com/daily/word"
                compact={true}
              />
            </>
          ) : (
            <button
              onClick={() => {
                const pid = Date.now();
                window.location.href = `/daily/word?mode=practice&pid=${pid}`;
              }}
              style={{
                backgroundColor: "var(--info)",
                color: "var(--text-on-accent)",
                border: "none",
                padding: "6px 14px",
                borderRadius: "var(--radius-full)",
                fontWeight: "var(--fw-bold)",
                fontSize: "var(--text-xs)",
                cursor: "pointer",
                width: "100%",
              }}
            >
              Play Practice Round Again
            </button>
          )}
        </div>
      )}
    </div>
  );
}
